/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import {
    authCreateUser,
    authDeleteRole,
    authDeleteUser,
    authListCapabilities,
    authListRoles,
    authListUsers,
    authSetUserDir,
    authSetUserPassword,
    authSetUserRole,
    authUpsertRole,
    AuthCapability,
    AuthRole,
    AuthUser
} from '../../data';

function UserManagementSettings() {
    const [users, setUsers] = useState<AuthUser[]>([]);
    const [roles, setRoles] = useState<AuthRole[]>([]);
    const [capabilities, setCapabilities] = useState<AuthCapability[]>([]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const [newUsername, setNewUsername] = useState('');
    const [newDir, setNewDir] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newRole, setNewRole] = useState('viewer');
    const [userDirs, setUserDirs] = useState<Record<number, string>>({});

    const [resetUser, setResetUser] = useState<AuthUser | null>(null);
    const [resetPasswordA, setResetPasswordA] = useState('');
    const [resetPasswordB, setResetPasswordB] = useState('');

    const [selectedRoleKey, setSelectedRoleKey] = useState('viewer');
    const [roleTitle, setRoleTitle] = useState('Viewer');
    const [roleCaps, setRoleCaps] = useState<string[]>([]);
    const [newRoleKey, setNewRoleKey] = useState('');
    const [newRoleTitle, setNewRoleTitle] = useState('');

    const selectedRole = useMemo(() => {
        return roles.find((r) => r.key === selectedRoleKey) || null;
    }, [roles, selectedRoleKey]);

    const refresh = async () => {
        setBusy(true);
        setError('');
        try {
            const [u, r, c] = await Promise.all([
                authListUsers(),
                authListRoles(),
                authListCapabilities()
            ]);
            setUsers(u);
            setUserDirs(Object.fromEntries(u.map((row) => [row.id, row.dir || ''])));
            setRoles(r);
            setCapabilities(c);

            const role = r.find((x) => x.key === selectedRoleKey) || r[0];
            if (role) {
                setSelectedRoleKey(role.key);
                setRoleTitle(role.title);
                setRoleCaps(role.capabilities.map((x) => x.key));
            }
            if (newRole === '' && r[0]) {
                setNewRole(r[0].key);
            }
        } catch (e: any) {
            setError(e?.message || 'Failed to load user management data');
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    useEffect(() => {
        if (selectedRole) {
            setRoleTitle(selectedRole.title);
            setRoleCaps(selectedRole.capabilities.map((x) => x.key));
        }
    }, [selectedRole]);

    const onCreateUser = async () => {
        if (!newUsername.trim() || newPassword.length < 6) {
            setError('New users need a username and password with at least 6 characters');
            return;
        }
        try {
            await authCreateUser(newUsername.trim(), newPassword, newRole, newDir.trim());
            setNewUsername('');
            setNewDir('');
            setNewPassword('');
            await refresh();
        } catch (e: any) {
            setError(e?.message || 'Failed creating user');
        }
    };

    const onResetPassword = async () => {
        if (!resetUser) return;
        if (resetPasswordA.length < 6 || resetPasswordA !== resetPasswordB) {
            setError('Password reset must match and be at least 6 characters');
            return;
        }
        try {
            await authSetUserPassword(resetUser.id, resetPasswordA);
            setResetUser(null);
            setResetPasswordA('');
            setResetPasswordB('');
            await refresh();
        } catch (e: any) {
            setError(e?.message || 'Failed resetting password');
        }
    };

    const onSaveRole = async () => {
        try {
            await authUpsertRole(selectedRoleKey, roleTitle || selectedRoleKey, roleCaps);
            await refresh();
        } catch (e: any) {
            setError(e?.message || 'Failed saving role');
        }
    };

    const onCreateRole = async () => {
        if (!newRoleKey.trim()) {
            setError('Role key is required');
            return;
        }
        try {
            await authUpsertRole(newRoleKey.trim(), (newRoleTitle || newRoleKey).trim(), []);
            setSelectedRoleKey(newRoleKey.trim());
            setNewRoleKey('');
            setNewRoleTitle('');
            await refresh();
        } catch (e: any) {
            setError(e?.message || 'Failed creating role');
        }
    };

    const onDeleteRole = async () => {
        if (selectedRoleKey === 'viewer' || selectedRoleKey === 'editor' || selectedRoleKey === 'admin') {
            setError('Built-in roles cannot be deleted');
            return;
        }
        try {
            await authDeleteRole(selectedRoleKey);
            setSelectedRoleKey('viewer');
            await refresh();
        } catch (e: any) {
            setError(e?.message || 'Failed deleting role');
        }
    };

    return (
        <div>
            <h4 className="mb-3">User Management</h4>
            {error ? <Alert variant="danger">{error}</Alert> : null}

            <Card className="mb-4">
                <Card.Header>Users</Card.Header>
                <Card.Body>
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Dir</th>
                                <th>Password</th>
                                <th>Created</th>
                                <th>Last Login</th>
                                <th>Login Failures</th>
                                <th>Role</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id}>
                                    <td>{u.username}</td>
                                    <td>
                                        <Form.Control
                                            size="sm"
                                            value={userDirs[u.id] ?? ''}
                                            onChange={(e) => {
                                                const next = e.target.value;
                                                setUserDirs((prev) => ({ ...prev, [u.id]: next }));
                                            }}
                                            onBlur={async (e) => {
                                                const next = e.target.value.trim();
                                                if (next === (u.dir || '')) {
                                                    return;
                                                }
                                                try {
                                                    await authSetUserDir(u.id, next);
                                                    await refresh();
                                                } catch (err: any) {
                                                    setError(err?.message || 'Failed updating user dir');
                                                }
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <Button size="sm" variant="outline-warning" onClick={() => setResetUser(u)}>
                                            Reset Password
                                        </Button>
                                    </td>
                                    <td>{u.created ? new Date(u.created * 1000).toLocaleString() : ''}</td>
                                    <td>{u.last_login ? new Date(u.last_login * 1000).toLocaleString() : ''}</td>
                                    <td>
                                        <Badge bg={u.login_failures > 0 ? 'danger' : 'secondary'}>{u.login_failures}</Badge>
                                    </td>
                                    <td>
                                        <Form.Select
                                            size="sm"
                                            value={u.role}
                                            onChange={async (e) => {
                                                try {
                                                    await authSetUserRole(u.id, e.target.value);
                                                    await refresh();
                                                } catch (err: any) {
                                                    setError(err?.message || 'Failed updating user role');
                                                }
                                            }}
                                        >
                                            {roles.map((r) => <option key={r.key} value={r.key}>{r.title}</option>)}
                                        </Form.Select>
                                    </td>
                                    <td>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={async () => {
                                                try {
                                                    await authDeleteUser(u.id);
                                                    await refresh();
                                                } catch (err: any) {
                                                    setError(err?.message || 'Failed deleting user');
                                                }
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <Row className="g-2 mt-2">
                        <Col md={3}>
                            <Form.Control
                                placeholder="username"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Control
                                placeholder="dir"
                                value={newDir}
                                onChange={(e) => setNewDir(e.target.value)}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Control
                                placeholder="password"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                                {roles.map((r) => <option key={r.key} value={r.key}>{r.title}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Button className="w-100" onClick={onCreateUser} disabled={busy}>Add User</Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header>Roles and Capabilities</Card.Header>
                <Card.Body>
                    <Row className="g-2 mb-3">
                        <Col md={4}>
                            <Form.Label>Role</Form.Label>
                            <Form.Select value={selectedRoleKey} onChange={(e) => setSelectedRoleKey(e.target.value)}>
                                {roles.map((r) => <option key={r.key} value={r.key}>{r.key}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Label>Title</Form.Label>
                            <Form.Control value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} />
                        </Col>
                        <Col md={4} className="d-flex align-items-end gap-2">
                            <Button onClick={onSaveRole}>Save Role</Button>
                            <Button variant="outline-danger" onClick={onDeleteRole}>Delete Role</Button>
                        </Col>
                    </Row>

                    <div className="mb-3">
                        {capabilities.map((c) => (
                            <Form.Check
                                key={c.key}
                                type="checkbox"
                                id={`cap-${c.key}`}
                                label={`${c.key} - ${c.title}`}
                                checked={roleCaps.includes(c.key)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setRoleCaps((prev) => Array.from(new Set([...prev, c.key])));
                                    } else {
                                        setRoleCaps((prev) => prev.filter((x) => x !== c.key));
                                    }
                                }}
                            />
                        ))}
                    </div>

                    <Row className="g-2">
                        <Col md={4}>
                            <Form.Control placeholder="new role key" value={newRoleKey} onChange={(e) => setNewRoleKey(e.target.value)} />
                        </Col>
                        <Col md={4}>
                            <Form.Control placeholder="new role title" value={newRoleTitle} onChange={(e) => setNewRoleTitle(e.target.value)} />
                        </Col>
                        <Col md={4}>
                            <Button onClick={onCreateRole}>Create Role</Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header>Capabilities Catalog</Card.Header>
                <Card.Body>
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Key</th>
                                <th>Title</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {capabilities.map((c) => (
                                <tr key={c.key}>
                                    <td>{c.key}</td>
                                    <td>{c.title}</td>
                                    <td>{c.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Modal show={!!resetUser} onHide={() => setResetUser(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Reset Password</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Reset password for <strong>{resetUser?.username}</strong></p>
                    <Form.Group className="mb-2">
                        <Form.Label>New Password</Form.Label>
                        <Form.Control type="password" value={resetPasswordA} onChange={(e) => setResetPasswordA(e.target.value)} />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Confirm Password</Form.Label>
                        <Form.Control type="password" value={resetPasswordB} onChange={(e) => setResetPasswordB(e.target.value)} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setResetUser(null)}>Cancel</Button>
                    <Button variant="primary" onClick={onResetPassword}>Reset</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default UserManagementSettings;
